import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { AuthContext } from "../../context/authContext";
import Header from "../topbotm/Header";
import Footer from "../topbotm/Footer";
import Sidebar from "../dashboard/sidebar";
import "../css/dashboard/DashBoardM.css";
import "../css/print/printLabelDocument.css";
import api from "../../api";
import { db } from "../../firebase";
import { collection, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import {
  MdAccountBalance,
  MdCalendarMonth,
  MdDescription,
  MdLocationOn,
  MdPerson,
  MdPersonPin,
  MdSupervisorAccount,
  MdViewModule,
} from "react-icons/md";

function safeText(value) {
  if (value === null || value === undefined) return "-";
  const s = String(value).trim();
  return s.length ? s : "-";
}

function formatDocumentDate(value) {
  if (!value) return "-";

  // ISO/string/number date
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-GB");
  return safeText(value);
}

function formatTpNoLine(tpRawFromDb) {
  const y = new Date().getFullYear();
  const raw = tpRawFromDb === null || tpRawFromDb === undefined ? "" : String(tpRawFromDb).trim();
  if (!raw || raw === "-") {
    return { kind: "tp", prefix: "TP No: ", emphasis: `TP/-/${y}` };
  }

  // If backend already returns something like "TP/123/2024", keep the middle segment only.
  const normalized = raw.replace(/^tp\//i, "");
  const parts = normalized.split("/").map((p) => p.trim()).filter(Boolean);
  let middle = parts.length >= 2 ? parts[0] : normalized;
  middle = middle.replace(/^tp\//i, "");

  return { kind: "tp", prefix: "TP No: ", emphasis: `TP/${middle}/${y}` };
}

function formatTokenNoLine(tokenRawFromDb) {
  const raw = tokenRawFromDb === null || tokenRawFromDb === undefined ? "" : String(tokenRawFromDb).trim();
  if (!raw || raw === "-") return "Token No: -";

  if (/^d/i.test(raw)) return `Token No: ${raw.toUpperCase()}`;

  const digits = raw.replace(/\D/g, "");
  if (!digits) return "Token No: -";

  return `Token No: D${digits}`;
}

/** Token number only (no "Token No:" prefix) for essential label. */
function formatTokenNoValue(tokenRawFromDb) {
  const raw = tokenRawFromDb === null || tokenRawFromDb === undefined ? "" : String(tokenRawFromDb).trim();
  if (!raw || raw === "-") return "-";
  if (/^d/i.test(raw)) return raw.toUpperCase();
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "-";
  return `D${digits}`;
}

function formatPartyWithCount(primaryName, totalCount) {
  const name = safeText(primaryName);
  const count = Number.isFinite(totalCount) ? totalCount : 0;
  if (name === "-" || count <= 1) return name;
  return `${name} (+${count - 1})`;
}

/* function formatWitnessWithCount(witnessArray) {
  const list = Array.isArray(witnessArray) ? witnessArray : [];
  const names = list
    .map((w) => safeText(w?.witness_Detail?.name || w?.name))
    .filter((n) => n && n !== "-");
  if (!names.length) return "-";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names[0]} & ${names[1]} (+${names.length - 2})`;
} */

function formatReferBy(referedBy) {
  if (!referedBy) return "-";
  if (typeof referedBy === "string") return safeText(referedBy);
  if (typeof referedBy === "object") {
    return safeText(referedBy?.name || referedBy?.label || referedBy?.value);
  }
  return safeText(referedBy);
}

function formatManagedBy(managedBy) {
  const list = Array.isArray(managedBy) ? managedBy : [];
  const names = list.map((v) => safeText(v)).filter((n) => n && n !== "-");
  if (!names.length) return "-";
  return names.join(", ");
}

function witnessNameAtIndex(witnessArray, idx) {
  const list = Array.isArray(witnessArray) ? witnessArray : [];
  if (idx < 0 || idx >= list.length) return "-";
  return safeText(list[idx]?.witness_Detail?.name || list[idx]?.name);
}

function fileLabelValueFontSize(value) {
  const t = safeText(value);
  const len = t === "-" ? 0 : String(t).length;
  if (len <= 18) return "11pt";
  if (len <= 28) return "10pt";
  if (len <= 40) return "9pt";
  return "8pt";
}

function fileLabelRowIcon(rowKey) {
  switch (rowKey) {
    case "fileApplicationNo":
      return <MdDescription />;
    case "fileBuyerName":
      return <MdPerson />;
    case "fileSellerName":
      return <MdPerson />;
    case "fileWitness1":
      return <MdPersonPin />;
    case "fileWitness2":
      return <MdPersonPin />;
    case "fileRegisterOffice":
      return <MdAccountBalance />;
    case "fileVillageName":
      return <MdLocationOn />;
    case "fileLayoutName":
      return <MdViewModule />;
    case "fileReferBy":
      return <MdPersonPin />;
    case "fileManagedBy":
      return <MdSupervisorAccount />;
    case "fileDocumentDate":
      return <MdCalendarMonth />;
    default:
      return <MdDescription />;
  }
}

function FileLabelBorderSvg() {
  return (
    <svg
      className="file-label-border-svg"
      viewBox="0 0 800 1200"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {/* Outer rounded frame */}
      <rect x="8" y="8" width="784" height="1184" rx="28" ry="28" />
    </svg>
  );
}

function formatDocumentNumberLine(registerOfficeRaw, documentNoRaw) {
  const y = new Date().getFullYear();
  const office = safeText(registerOfficeRaw);
  const docRaw = safeText(documentNoRaw);

  if (office === "-" && docRaw === "-") return `R/-/-/${y}`;

  // Avoid duplicating year if backend already includes it.
  const normalizedDoc = docRaw === "-" ? "-" : String(docRaw).split("/")[0].trim() || "-";
  const normalizedOffice = office === "-" ? "-" : office.replace(/\s+/g, " ").trim();

  return `R/${normalizedOffice}/${normalizedDoc}/${y}`;
}

function documentYearFromDocData(docData) {
  const raw = docData?.document_date || docData?.documentDate || docData?.date;
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return String(d.getFullYear());
}

function formatRegisteredDocumentNumber(docData) {
  const y = documentYearFromDocData(docData);
  const raw =
    docData?.document_registered?.document_no ||
    docData?.document_pending?.document_no ||
    docData?.documentNumber;

  const docRaw = safeText(raw);
  if (docRaw === "-" || !y) return docRaw;

  // Remove any existing "/year" tail if backend already includes it.
  const base = String(docRaw).split("/")[0].trim() || docRaw;
  return `${base}/${y}`;
}

async function fetchApplicationNoFromFirebase({ appNo, id }) {
  const typed = String(appNo || "").trim();
  if (!typed) return "";

  const candidates = [
    "Customer",
    "Customers",
    "customer",
    "customers",
    "reg_docs",
    "documents",
    "document",
    "doc",
    "docs",
  ];

  async function tryGetDocByKey(collectionName, key) {
    if (!key) return "";
    try {
      const snap = await getDoc(doc(db, collectionName, String(key)));
      if (!snap.exists()) return "";
      const data = snap.data() || {};
      const found = String(data.application_no || data.applicationNo || data.application_id || "").trim();
      return found;
    } catch {
      return "";
    }
  }

  for (const name of candidates) {
    const byId = id ? await tryGetDocByKey(name, id) : "";
    if (byId) return byId;
    const byAppNoAsId = await tryGetDocByKey(name, typed);
    if (byAppNoAsId) return byAppNoAsId;
  }

  // Fallback: query by field, in case doc id isn't the same as appNo/id.
  for (const name of candidates) {
    try {
      const q = query(collection(db, name), where("application_no", "==", typed), limit(1));
      const snaps = await getDocs(q);
      if (snaps.empty) continue;
      const data = snaps.docs[0].data() || {};
      const found = String(data?.application_no || data?.applicationNo || data?.application_id || "").trim();
      if (found) return found;
    } catch {
      // ignore
    }
  }

  return "";
}

function extractTokenTimeFromDocData(data) {
  if (!data || typeof data !== "object") return "";
  const ti = data.token_initiated;
  if (!ti || typeof ti !== "object") return "";
  return String(ti.time ?? ti.Time ?? "").trim();
}

/** Token slot time from Firestore Customer-style docs (same collections as application no lookup). */
async function fetchTokenTimeFromFirebase({ appNo, id }) {
  const typed = String(appNo || "").trim();
  if (!typed) return "";

  const candidates = [
    "Customer",
    "Customers",
    "customer",
    "customers",
    "reg_docs",
    "documents",
    "document",
    "doc",
    "docs",
  ];

  async function tryGetTimeByKey(collectionName, key) {
    if (!key) return "";
    try {
      const snap = await getDoc(doc(db, collectionName, String(key)));
      if (!snap.exists()) return "";
      return extractTokenTimeFromDocData(snap.data() || {});
    } catch {
      return "";
    }
  }

  for (const name of candidates) {
    const byId = id ? await tryGetTimeByKey(name, id) : "";
    if (byId) return byId;
    const byAppNoAsId = await tryGetTimeByKey(name, typed);
    if (byAppNoAsId) return byAppNoAsId;
  }

  for (const name of candidates) {
    try {
      const q = query(collection(db, name), where("application_no", "==", typed), limit(1));
      const snaps = await getDocs(q);
      if (snaps.empty) continue;
      const t = extractTokenTimeFromDocData(snaps.docs[0].data() || {});
      if (t) return t;
    } catch {
      // ignore
    }
  }

  return "";
}

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error("timeout")), ms);
    promise
      .then((v) => resolve(v))
      .catch((e) => reject(e))
      .finally(() => window.clearTimeout(t));
  });
}

function buildLabels(docData, selectedApplicationNo) {
  // Backend doc shape (based on existing pages)
  const applicationNo = safeText(
    docData?.application_no || docData?.applicationNo || docData?.application_id || selectedApplicationNo,
  );
  const documentDate = formatDocumentDate(docData?.document_date || docData?.documentDate || docData?.date);
  const buyerName = formatPartyWithCount(
    docData?.buyers?.[0]?.buyer_Detail?.name || docData?.buyerName,
    docData?.buyers?.length || 0,
  );
  const sellerName = formatPartyWithCount(
    docData?.sellers?.[0]?.seller_Detail?.name || docData?.sellerName,
    docData?.sellers?.length || 0,
  );
  const documentType = safeText(docData?.register_document || docData?.documentType);
  const registerOffice = safeText(docData?.register_office || docData?.registerOffice);
  const tpLine = formatTpNoLine(docData?.deed_no?.tp_no ?? docData?.tpNo);
  const tokenLine = formatTokenNoLine(docData?.token_initiated?.token_no ?? docData?.tokenNo);
  const rawDocumentNo =
    docData?.document_registered?.document_no ||
    docData?.document_pending?.document_no ||
    docData?.documentNumber;
  const documentNumber = formatDocumentNumberLine(registerOffice, rawDocumentNo);

  return [
    { key: "applicationNo", title: "", value: applicationNo },
    { key: "documentDate", title: "", value: documentDate },
    { key: "buyerName", title: "", value: buyerName },
    { key: "sellerName", title: "", value: sellerName },
    { key: "documentType", title: "", value: documentType },
    { key: "registerOffice", title: "", value: registerOffice },
    { key: "tpToken", title: "", lines: [tpLine, tokenLine] },
    { key: "documentNumber", title: "", value: documentNumber },
  ];
}

function fontSizeForLabelValue({ value, lines }) {
  const lineTextLen = (line) => {
    if (line === null || line === undefined) return 0;
    if (typeof line === "string") return String(line).trim().length;
    if (typeof line === "object" && line.kind === "tp") {
      const p = String(line.prefix ?? "");
      const e = String(line.emphasis ?? "");
      return `${p}${e}`.length;
    }
    return String(line).length;
  };

  const lineLens = Array.isArray(lines) ? lines.map(lineTextLen).filter((n) => n > 0) : [];
  const raw = value === null || value === undefined ? "" : String(value);
  const t = raw.trim();
  const len = lineLens.length ? Math.max(...lineLens) : t.length;
  const isTwoLineBlock = Array.isArray(lines) && lines.length > 1;

  if (isTwoLineBlock) {
    if (len <= 12) return "18pt";
    if (len <= 18) return "16pt";
    if (len <= 26) return "14pt";
    if (len <= 36) return "12pt";
    return "11pt";
  }

  if (len <= 10) return "22pt";
  if (len <= 16) return "20pt";
  if (len <= 24) return "18pt";
  if (len <= 34) return "16pt";
  if (len <= 48) return "14pt";
  return "12pt";
}

const LABEL_PRINT_MODES = ["essential", "full", "registered", "file"];

/** Full-size sheet (80×120mm) bands. */
const FULL_SIZE_LABEL_SLOTS_MM = [
  { key: "documentDate", topMm: 3, endMm: 8 },
  { key: "sellerName", topMm: 48, endMm: 53 },
  { key: "buyerName", topMm: 69, endMm: 74 },
  { key: "documentType", topMm: 91, endMm: 96 },
  { key: "registerOffice", topMm: 110, endMm: 115 },
];

function LabelValueBlock({ labelItem: l }) {
  return (
    <div
      className={[
        "label-field-value",
        Array.isArray(l.lines) && l.lines.length ? "label-field-value--lines" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        "--label-font-size": fontSizeForLabelValue({
          value: l.value,
          lines: l.lines,
        }),
      }}
    >
      {Array.isArray(l.lines) && l.lines.length ? (
        <div className="label-field-lines">
          {l.lines.map((line, idx) => (
            <div
              className={[
                "label-field-line",
                typeof line === "string" && idx === 1 ? "label-field-line--token" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              key={`${l.key}-line-${idx}`}
            >
              {typeof line === "string" ? (
                safeText(line)
              ) : line?.kind === "tp" ? (
                <>
                  <span className="label-line-prefix">{line.prefix}</span>
                  <span className="label-line-tp-emphasis">{line.emphasis}</span>
                </>
              ) : (
                safeText(line)
              )}
            </div>
          ))}
        </div>
      ) : (
        l.value
      )}
    </div>
  );
}

export default function PrintLabelDocument() {
  const { user } = useContext(AuthContext);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [applicationOptions, setApplicationOptions] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [docData, setDocData] = useState(null);
  const [error, setError] = useState("");
  const [refreshFeedback, setRefreshFeedback] = useState("idle"); // idle | success | error
  const [refreshPress, setRefreshPress] = useState(false);
  const [labelPrintMode, setLabelPrintMode] = useState("full");
  const [essentialTimeFromFirestore, setEssentialTimeFromFirestore] = useState("");

  const documentsCacheRef = useRef({ loadedAt: 0, items: [] });
  const isMountedRef = useRef(true);
  const refreshFeedbackTimerRef = useRef(null);
  const refreshPressTimerRef = useRef(null);

  const labels = useMemo(() => buildLabels(docData, selectedId), [docData, selectedId]);
  const fileLabelRows = useMemo(() => {
    if (!docData) return [];

    const applicationNo = safeText(
      docData?.application_no || docData?.applicationNo || docData?.application_id || selectedId,
    );
    const documentDate = formatDocumentDate(docData?.document_date || docData?.documentDate || docData?.date);
    const buyerName = formatPartyWithCount(
      docData?.buyers?.[0]?.buyer_Detail?.name || docData?.buyerName,
      docData?.buyers?.length || 0,
    );
    const sellerName = formatPartyWithCount(
      docData?.sellers?.[0]?.seller_Detail?.name || docData?.sellerName,
      docData?.sellers?.length || 0,
    );
    const registerOffice = safeText(docData?.register_office || docData?.registerOffice);

    return [
      { key: "fileApplicationNo", label: "Application No", value: applicationNo },
      { key: "fileBuyerName", label: "Buyer Name", value: buyerName },
      { key: "fileSellerName", label: "Seller Name", value: sellerName },
      { key: "fileWitness1", label: "Witness 1", value: witnessNameAtIndex(docData?.witness, 0) },
      { key: "fileWitness2", label: "Witness 2", value: witnessNameAtIndex(docData?.witness, 1) },
      { key: "fileRegisterOffice", label: "Register Office", value: registerOffice },
      { key: "fileVillageName", label: "Village", value: safeText(docData?.village) },
      { key: "fileLayoutName", label: "Layout Name", value: safeText(docData?.layout_name || docData?.layoutName) },
      { key: "fileReferBy", label: "Refered By", value: formatReferBy(docData?.refered_by || docData?.referBy) },
      { key: "fileManagedBy", label: "Manage By", value: formatManagedBy(docData?.managed_By || docData?.managedBy) },
      { key: "fileDocumentDate", label: "Date", value: documentDate },
    ];
  }, [docData, selectedId]);
  const registeredLabelLines = useMemo(() => {
    if (!docData) return null;
    const registerOffice = safeText(docData?.register_office || docData?.registerOffice);
    const documentNumberWithYear = formatRegisteredDocumentNumber(docData);
    return {
      headline: "REGISTERED",
      registerOffice,
      documentNumberWithYear,
    };
  }, [docData]);
  const essentialLabelItems = useMemo(() => {
    if (!docData) return [];
    const applicationNo = safeText(
      docData.application_no || docData.applicationNo || docData.application_id || selectedId,
    );
    const tpLine = formatTpNoLine(docData?.deed_no?.tp_no ?? docData?.tpNo);
    const tpValue = tpLine?.kind === "tp" ? String(tpLine.emphasis ?? "").trim() : "-";
    const tokenValue = formatTokenNoValue(docData?.token_initiated?.token_no ?? docData?.tokenNo);
    const apiTime = String(docData?.token_initiated?.time ?? "").trim();
    const fbTime = String(essentialTimeFromFirestore || "").trim();
    const timing = fbTime || apiTime;
    const timeValue = timing ? safeText(timing) : "-";
    return [
      { key: "essentialAppNo", value: applicationNo },
      { key: "essentialTp", value: safeText(tpValue) },
      { key: "essentialToken", value: tokenValue },
      { key: "essentialTime", value: timeValue },
    ];
  }, [docData, selectedId, essentialTimeFromFirestore]);
  const canPrint = Boolean(docData) && !loadingDoc && !error;

  const refreshApplications = useCallback(async ({ force, withFeedback }) => {
    let ok = false;
    try {
      if (!isMountedRef.current) return ok;
      setLoadingApplications(true);

      const now = Date.now();
      const cache = documentsCacheRef.current;

      const shouldFetch = force || !cache.loadedAt || now - cache.loadedAt > 60_000;
      if (shouldFetch) {
        const res = await api.get("/view_Customer");
        const items = res?.data?.reg_docs || [];
        documentsCacheRef.current = { loadedAt: now, items };
      }

      const items = documentsCacheRef.current.items;
      const uniqueAppNos = Array.from(
        new Set(items.map((d) => String(d?.application_no || "").trim()).filter(Boolean)),
      ).sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: "base" }));

      if (!isMountedRef.current) return ok;
      setApplicationOptions(uniqueAppNos);
      ok = true;
    } catch (e) {
      if (!isMountedRef.current) return ok;
      setApplicationOptions([]);
      ok = false;
    } finally {
      if (!isMountedRef.current) return ok;
      setLoadingApplications(false);
    }

    if (withFeedback && isMountedRef.current) {
      if (refreshFeedbackTimerRef.current) {
        window.clearTimeout(refreshFeedbackTimerRef.current);
        refreshFeedbackTimerRef.current = null;
      }

      setRefreshFeedback(ok ? "success" : "error");
      refreshFeedbackTimerRef.current = window.setTimeout(() => {
        if (!isMountedRef.current) return;
        setRefreshFeedback("idle");
        refreshFeedbackTimerRef.current = null;
      }, 900);
    }

    return ok;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    refreshApplications({ force: false, withFeedback: false });

    return () => {
      isMountedRef.current = false;
      if (refreshFeedbackTimerRef.current) {
        window.clearTimeout(refreshFeedbackTimerRef.current);
        refreshFeedbackTimerRef.current = null;
      }
      if (refreshPressTimerRef.current) {
        window.clearTimeout(refreshPressTimerRef.current);
        refreshPressTimerRef.current = null;
      }
    };
  }, [refreshApplications]);

  async function fetchByApplicationNo(appNo) {
    const typed = appNo.trim();
    if (!typed) return;

    setError("");
    setLoadingDoc(true);
    setDocData(null);
    setEssentialTimeFromFirestore("");
    setSelectedId(typed);

    try {
      // Ensure we have a fresh-enough cache to resolve appNo -> internal id
      const now = Date.now();
      const cache = documentsCacheRef.current;
      if (!cache.loadedAt || now - cache.loadedAt > 60_000) {
        const res = await api.get("/view_Customer");
        documentsCacheRef.current = { loadedAt: now, items: res?.data?.reg_docs || [] };
      }

      const items = documentsCacheRef.current.items;
      const exact = items.find((d) => String(d?.application_no || "").trim() === typed);
      if (!exact?.id) {
        setError("Invalid Application No.");
        return;
      }

      const view = await api.get(`/viewone_Customer/${exact.id}`);
      const docFromApi = view?.data?.document || null;
      if (!docFromApi) {
        setError("No data found for this Application No.");
        return;
      }

      // Render immediately from API, then enrich from Firebase without blocking the UI.
      setDocData({ ...docFromApi, application_no: docFromApi?.application_no || typed });

      void withTimeout(fetchApplicationNoFromFirebase({ appNo: typed, id: exact.id }), 2500)
        .then((fbAppNo) => {
          const normalized = String(fbAppNo || "").trim();
          if (!normalized) return;
          if (!isMountedRef.current) return;
          setDocData((prev) => {
            if (!prev) return prev;
            if (String(prev.application_no || "").trim() === normalized) return prev;
            return { ...prev, application_no: normalized };
          });
        })
        .catch(() => {
          // Ignore Firebase lookup failures/timeouts (printing can still proceed).
        });

      void withTimeout(fetchTokenTimeFromFirebase({ appNo: typed, id: exact.id }), 2500)
        .then((fbTime) => {
          const normalized = String(fbTime || "").trim();
          if (!normalized) return;
          if (!isMountedRef.current) return;
          setEssentialTimeFromFirestore(normalized);
        })
        .catch(() => {
          // Ignore Firebase timing lookup failures (API token_initiated.time still used).
        });
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Session expired. Please login again.");
      } else {
        setError("Failed to fetch data. Please try again.");
      }
    } finally {
      setLoadingDoc(false);
    }
  }

  function onSubmit(e) {
    e.preventDefault();
    fetchByApplicationNo(search);
  }

  function onPrint() {
    if (!canPrint) return;
    window.print();
  }

  return (
    <div className="dashboard-page print-label-page">
      <Header />
      <div className="dashboard-wrapper">
        <div className='d-none' style={{ backgroundColor: "#4a127e" }}>
          <Sidebar user={user} />
        </div>

        <div className="main-area">
          <div className="content-padding">
            <div className="no-print print-label-toolbar">
              <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                <h3 className="m-0">Print Label</h3>
                <button
                  type="button"
                  className={[
                    "btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1 print-refresh-btn",
                    refreshPress ? "print-refresh-btn--press" : "",
                    refreshFeedback === "success" ? "print-refresh-btn--success" : "",
                    refreshFeedback === "error" ? "print-refresh-btn--error" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onMouseDown={() => {
                    if (loadingApplications) return;
                    setRefreshPress(true);
                    if (refreshPressTimerRef.current) window.clearTimeout(refreshPressTimerRef.current);
                    refreshPressTimerRef.current = window.setTimeout(() => {
                      if (!isMountedRef.current) return;
                      setRefreshPress(false);
                      refreshPressTimerRef.current = null;
                    }, 140);
                  }}
                  onClick={() => void refreshApplications({ force: true, withFeedback: true })}
                  disabled={loadingApplications}
                  title="Refresh Application IDs"
                  aria-label="Refresh Application IDs"
                >
                  <RefreshCw size={16} className={loadingApplications ? "spin" : ""} />
                  Refresh
                </button>
              </div>

              <form onSubmit={onSubmit} className="row g-2 align-items-end">
                <div className="col-12 col-md-7 position-relative">
                  <label className="form-label mb-1">Application ID</label>
                  <select
                    className="form-select"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    disabled={loadingApplications}
                  >
                    <option value="">{loadingApplications ? "Loading Application IDs…" : "Select Application ID"}</option>
                    {applicationOptions.map((appNo) => (
                      <option key={appNo} value={appNo}>
                        {appNo}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-5 d-flex gap-2">
                  <button type="submit" className="btn btn-primary w-100" disabled={!search.trim() || loadingDoc}>
                    {loadingDoc ? "Loading…" : "Fetch"}
                  </button>
                  <button type="button" className="btn btn-dark w-100" onClick={onPrint} disabled={!canPrint}>
                    Print
                  </button>
                </div>
              </form>

              <div className="mt-2 small text-muted">
                {selectedId ? (
                  <span>
                    Selected: <strong>{selectedId}</strong>
                  </span>
                ) : (
                  <span>Search and select an Application ID to preview labels.</span>
                )}
              </div>

              {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}

              <div className="print-label-layout-tabs no-print mt-3">
                <ul className="nav nav-tabs mb-0" role="tablist" aria-label="Label layout">
                  {LABEL_PRINT_MODES.map((mode) => {
                    const id = `print-label-tab-${mode}`;
                    const selected = labelPrintMode === mode;
                    const label =
                      mode === "essential"
                        ? "Essential Label"
                        : mode === "file"
                          ? "File Label"
                        : mode === "registered"
                          ? "Registered Label"
                          : "Full Size Label";
                    return (
                      <li className="nav-item" key={mode} role="presentation">
                        <button
                          type="button"
                          className={["nav-link", selected ? "active" : ""].filter(Boolean).join(" ")}
                          id={id}
                          role="tab"
                          aria-selected={selected}
                          tabIndex={selected ? 0 : -1}
                          onClick={() => setLabelPrintMode(mode)}
                        >
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div
              id="print-label-area"
              aria-label="Label print area"
              data-label-print-mode={labelPrintMode}
            >
              <div className="label-preview-wrap">
                {docData ? (
                  labelPrintMode === "full" ? (
                    <div className="label-sheet--combined label-sheet--combined-fixed-slots">
                      <div className="label-combined-stack label-combined-stack--fixed-slots">
                        {FULL_SIZE_LABEL_SLOTS_MM.map((slot) => {
                          const l = labels.find((item) => item.key === slot.key);
                          if (!l) return null;
                          const heightMm = slot.endMm - slot.topMm;
                          return (
                            <div
                              className="label-combined-row label-combined-row--fixed-slot"
                              key={l.key}
                              style={{
                                top: `${slot.topMm}mm`,
                                minHeight: `${heightMm}mm`,
                                height: "auto",
                              }}
                            >
                              {l.title ? <div className="label-field-name">{l.title}</div> : null}
                              <LabelValueBlock labelItem={l} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : labelPrintMode === "file" ? (
                    <div className="label-sheet--combined label-sheet--file">
                      <div className="file-label-frame">
                        <FileLabelBorderSvg />
                        <div className="file-label-title">DOCUMENT LABEL</div>
                        <div className="file-label-ornament" aria-hidden />

                        <div className="file-label-table" role="table" aria-label="File label">
                          {fileLabelRows.map((row, idx) => (
                            <div className="file-label-row" role="row" key={row.key} data-row-index={idx}>
                              <div className="file-label-cell file-label-cell--icon" role="cell" aria-hidden>
                                {fileLabelRowIcon(row.key)}
                              </div>
                              <div className="file-label-cell file-label-cell--label" role="cell">
                                {row.label}
                              </div>
                              <div
                                className="file-label-cell file-label-cell--value"
                                role="cell"
                                style={{ "--file-value-font-size": fileLabelValueFontSize(row.value) }}
                              >
                                {safeText(row.value)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : labelPrintMode === "registered" ? (
                    <div className="label-sheet--combined label-sheet--registered">
                      <div className="label-registered-content">
                        <div className="label-registered-line label-registered-line--headline">
                          {registeredLabelLines?.headline || "REGISTERED"}
                        </div>
                        <div className="label-registered-line label-registered-line--office">
                          {registeredLabelLines?.registerOffice || "-"}
                        </div>
                        <div className="label-registered-line label-registered-line--docno">
                          {registeredLabelLines?.documentNumberWithYear || "-"}
                        </div>
                      </div>
                    </div>
                  ) : labelPrintMode === "essential" ? (
                    <div className="label-sheet--combined label-sheet--essential">
                      <div className="label-essential-printable">
                        {essentialLabelItems.map((item) => (
                          <div className="label-essential-row" key={item.key}>
                            <LabelValueBlock labelItem={item} />
                          </div>
                        ))}
                      </div>
                      <div className="label-essential-lower-blank" aria-hidden />
                    </div>
                  ) : null
                ) : (
                  <div className="text-center text-muted py-4 no-print">
                    No data loaded. Labels will appear here after fetching an Application ID.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
